/**
 * Numerology Calculator Inline Module
 * Simplified version for use within product forms
 */

import "./dropdown.css";

class NumerologyCalculatorInline {
  constructor(wrapper) {
    // Check if already initialized
    if (wrapper._numerologyInitialized) {
      return;
    }
    wrapper._numerologyInitialized = true;
    document.querySelector(".numerology-cart-button")?.classList.add("hidden");
    document.querySelector(".alternate_links")?.classList.add("hidden");

    this.wrapper = wrapper;
    this.form = wrapper.querySelector(".numerology-form");
    this.resultElement = wrapper.querySelector(".numerology-result");
    this.meaningElement = wrapper.querySelector(".numerology-meaning");
    this.lifeNumberElement = wrapper.querySelector(".life-number");
    this.lifeNumberDisplayElement = wrapper.querySelector(
      ".life-number-display"
    );

    // Dropdown elements
    this.dayInput = wrapper.querySelector(".birth-day");
    this.monthInput = wrapper.querySelector(".birth-month");
    this.yearInput = wrapper.querySelector(".birth-year");
    this.dropdowns = wrapper.querySelectorAll(".custom-dropdown");

    this.attachEventListeners();
    this.initializeDropdowns();
  }

  attachEventListeners() {
    if (this.form) {
      // Since this is now a div instead of a form, we only need the button click handler
      const calculateBtn = this.wrapper.querySelector(".calculate-btn");
      if (calculateBtn) {
        calculateBtn.addEventListener("click", (e) => {
          e.preventDefault();

          // Check if button is in recalculate state
          if (calculateBtn.dataset.state === "recalculate") {
            this.resetCalculator();
          } else {
            this.handleFormSubmit(e);
          }
        });
      }
    }

    // Add click event listener to document to close dropdowns when clicking outside
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".custom-dropdown")) {
        this.closeAllDropdowns();
      }
    });

    // Note: Removed automatic reset functionality to prevent conflicts with cart button visibility
  }

  addCartButtonShowStyles() {
    // Always remove existing styles first, then add fresh ones
    this.removeCartButtonStyles();

    // Create CSS to force cart buttons to show
    const style = document.createElement("style");
    style.id = "numerology-cart-button-styles";
    style.textContent = `
      .numerology-cart-button,
      .btn-primary,
      .product-form__cart,
      button[name='add'],
      button[type='submit'],
      .add-to-cart-button,
      .product-form__buttons button {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      .alternate_links {
        display: block !important;
        visibility: visible !important;
      }
      .numerology-cart-message {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    console.log("Added CSS styles to force cart button visibility");
  }

  removeCartButtonStyles() {
    const existingStyle = document.getElementById(
      "numerology-cart-button-styles"
    );
    if (existingStyle) {
      existingStyle.remove();
      console.log("Removed cart button override styles");
    }
  }

  lockDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector(".dropdown-trigger");
      if (trigger) {
        // Disable the dropdown trigger
        trigger.disabled = true;
        trigger.style.pointerEvents = "none";

        // Add a locked class for styling
        trigger.classList.add("dropdown-locked");

        // Store original click handlers to prevent interaction
        trigger.dataset.locked = "true";
      }
    });
  }

  unlockDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector(".dropdown-trigger");
      if (trigger) {
        // Re-enable the dropdown trigger
        trigger.disabled = false;
        trigger.style.pointerEvents = "";

        // Remove locked class
        trigger.classList.remove("dropdown-locked");

        // Remove locked marker
        trigger.removeAttribute("data-locked");
      }
    });
  }

  initializeDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector(".dropdown-trigger");
      const menu = dropdown.querySelector(".dropdown-menu");
      const options = dropdown.querySelectorAll(".dropdown-option");
      const text = dropdown.querySelector(".dropdown-text");
      const arrow = trigger.querySelector("svg");

      // Toggle dropdown on trigger click
      trigger.addEventListener("click", (e) => {
        e.stopPropagation();

        // Check if dropdown is locked
        if (trigger.dataset.locked === "true") {
          return; // Don't open if locked
        }

        const isOpen = !menu.classList.contains("hidden");

        // Close all dropdowns first
        this.closeAllDropdowns();

        // Open this dropdown if it was closed
        if (!isOpen) {
          menu.classList.remove("hidden");
          arrow.style.transform = "rotate(180deg)";
        }
      });

      // Handle option selection
      options.forEach((option) => {
        option.addEventListener("click", (e) => {
          // Check if dropdown is locked
          if (trigger.dataset.locked === "true") {
            return; // Don't allow selection if locked
          }

          const value = option.dataset.value;
          const displayText = option.textContent;

          // Update the trigger
          trigger.dataset.value = value;
          text.textContent = displayText;

          // Change text color to black when value is selected
          text.classList.remove("text-gray-500");
          text.classList.add("text-anna-black");

          // Clear validation error for this field
          trigger.classList.remove("border-red-500", "border-2");
          trigger.classList.add("border-anna-black");

          // Check if all fields are now filled and hide validation message if so
          const allFieldsFilled = this.checkIfAllFieldsFilled();
          if (allFieldsFilled) {
            this.hideValidationMessage();
          }

          // Close the dropdown
          menu.classList.add("hidden");
          arrow.style.transform = "rotate(0deg)";

          // Trigger change event for validation
          trigger.dispatchEvent(new Event("change"));
        });
      });
    });
  }

  closeAllDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      const menu = dropdown.querySelector(".dropdown-menu");
      const arrow = dropdown.querySelector(".dropdown-trigger svg");
      menu.classList.add("hidden");
      if (arrow) {
        arrow.style.transform = "rotate(0deg)";
      }
    });
  }

  handleFormSubmit(e) {
    e.preventDefault();

    const dayValue = this.dayInput.dataset.value || this.dayInput.value || "";
    const monthValue =
      this.monthInput.dataset.value || this.monthInput.value || "";
    const yearValue =
      this.yearInput.dataset.value || this.yearInput.value || "";

    const day = dayValue ? parseInt(dayValue) : null;
    const month = monthValue ? parseInt(monthValue) : null;
    const year = yearValue ? parseInt(yearValue) : null;

    if (!this.validateInputs(day, month, year)) {
      return;
    }

    const result = this.calculateNumerology(day, month, year);
    this.displayResult(result);
  }

  validateInputs(day, month, year) {
    let isValid = true;
    let errorMessage = "";

    // Clear any existing error states first
    this.clearValidationErrors();

    // Check if fields are filled
    if (!day) {
      this.highlightField(this.dayInput);
      isValid = false;
    }
    if (!month) {
      this.highlightField(this.monthInput);
      isValid = false;
    }
    if (!year) {
      this.highlightField(this.yearInput);
      isValid = false;
    }

    // If any fields are missing, show appropriate message
    if (!isValid) {
      errorMessage = this.getValidationText("valid_date");
      this.showValidationMessage(errorMessage);
      return false;
    }

    // Validate date existence
    const date = new Date(year, month - 1, day);
    if (
      date.getDate() !== day ||
      date.getMonth() !== month - 1 ||
      date.getFullYear() !== year
    ) {
      // Highlight all fields for invalid date
      this.highlightField(this.dayInput);
      this.highlightField(this.monthInput);
      this.highlightField(this.yearInput);
      this.showValidationMessage(this.getValidationText("valid_date"));
      return false;
    }

    return true;
  }

  highlightField(fieldElement) {
    if (fieldElement) {
      fieldElement.classList.add("border-red-500", "border");
      fieldElement.classList.remove("border-anna-black");
    }
  }

  showValidationMessage(message) {
    // Remove any existing validation message
    this.hideValidationMessage();

    // Create validation message element
    const messageElement = document.createElement("div");
    const messageHolder = this.form.querySelector("#validation-message-holder");
    messageElement.id = "numerology-validation-message";
    messageElement.className = "mt-3 rounded-md text-red-600 text-xs";
    messageElement.textContent = message;

    // Insert after the calculator form
    if (this.form && this.form.parentNode) {
      messageHolder.innerHTML = messageElement.outerHTML;
    }
  }

  hideValidationMessage() {
    const existingMessage = document.getElementById(
      "numerology-validation-message"
    );
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  checkIfAllFieldsFilled() {
    const dayValue = this.dayInput.dataset.value || this.dayInput.value || "";
    const monthValue =
      this.monthInput.dataset.value || this.monthInput.value || "";
    const yearValue =
      this.yearInput.dataset.value || this.yearInput.value || "";

    return dayValue && monthValue && yearValue;
  }

  getValidationText(key) {
    // Access validation texts from window.numerologyTexts like other translations
    const validationTexts = window.numerologyTexts?.validation;

    return validationTexts[key];
  }

  clearValidationErrors() {
    [this.dayInput, this.monthInput, this.yearInput].forEach((field) => {
      if (field) {
        field.classList.remove("border-red-500");
        field.classList.add("border-anna-black");
      }
    });
    // Also hide any validation messages
    this.hideValidationMessage();
  }

  calculateNumerology(day, month, year) {
    // Step 1: Reduce each component to single digit
    const dayReduced = this.reduceToSingleDigit(day);
    const monthReduced = this.reduceToSingleDigit(month);
    const yearReduced = this.reduceToSingleDigit(year);

    // Step 2: Add all reduced numbers together
    const sum = dayReduced.result + monthReduced.result + yearReduced.result;
    const finalReduction = this.reduceToSingleDigit(sum);

    return {
      lifeNumber: finalReduction.result,
      steps: [], // Simplified version doesn't show calculation steps
      meaning: this.getNumerologyMeaning(finalReduction.result),
    };
  }

  reduceToSingleDigit(num) {
    const steps = [num];
    let current = num;

    while (current >= 10) {
      const digits = current.toString().split("").map(Number);
      current = digits.reduce((sum, digit) => sum + digit, 0);
      steps.push(current);
    }

    return {
      result: current,
      steps: steps,
    };
  }

  getNumerologyMeaning(number) {
    // Use translations from window.numerologyTranslations
    const translations = window.numerologyTranslations?.[number.toString()];

    if (translations) {
      return {
        title: translations.title || "",
        description: translations.description || "",
        traits: [
          translations.trait_1,
          translations.trait_2,
          translations.trait_3,
          translations.trait_4,
          translations.trait_5,
        ].filter((trait) => trait && trait !== ""), // Remove empty traits
        challenges: translations.challenge || "",
      };
    }

    // Return empty structure if no translations found
    return {
      title: "",
      description: "",
      traits: [],
      challenges: "",
    };
  }

  displayResult(result) {
    // Transform calculate button to show "recalculate" text but keep same functionality
    const calculateBtn = this.wrapper.querySelector(".calculate-btn");
    if (calculateBtn) {
      calculateBtn.classList.remove(
        "bg-anna-black",
        "text-white",
        "hover:bg-anna-black"
      );
      calculateBtn.classList.add(
        "bg-white",
        "text-anna-black",
        "border",
        "border-anna-black",
        "md:hover:!bg-anna-black",
        "md:hover:text-white"
      );
      calculateBtn.textContent =
        window.numerologyTexts?.buttons?.recalculate || "Neu berechnen";
      calculateBtn.setAttribute("data-state", "recalculate");
    }

    // Display result
    if (this.lifeNumberElement) {
      this.lifeNumberElement.textContent = result.lifeNumber;
    }
    this.lifeNumberDisplayElement.textContent = result.lifeNumber;

    // Display meaning - simplified format for inline version
    this.meaningElement.innerHTML = `
      <div class="flex flex-wrap gap-2">
        ${result.meaning.title} ${result.meaning.traits
      .map((trait) => `${trait}`)
      .join(", ")}
      </div>
      <p class="text-xs mb-6 leading-relaxed">${result.meaning.description}</p>
    `;

    this.resultElement.classList.remove("hidden");

    // Auto-select the calculated life number in the product option FIRST
    const lifeNumberStr = result.lifeNumber.toString();
    // Make all dropdown text appear gray after calculation to maintain placeholder illusion
    this.dropdowns.forEach((dropdown) => {
      const text = dropdown.querySelector(".dropdown-text");
      if (text) {
        text.classList.remove("text-anna-black");
        text.classList.add("text-anna-gray");
      }
    });

    // Lock all dropdowns after calculation
    this.lockDropdowns();

    // Try options-input first (visual button options)
    const numberOptionsInput = document.querySelector("options-input");
    if (numberOptionsInput) {
      const selectElement = numberOptionsInput.querySelector(
        'select[data-option_type*="Number"], select[data-option_type*="number"], select[data-option_type*="Zahl"], select[data-option_type*="zahl"]'
      );
      if (selectElement) {
        const targetOption = numberOptionsInput.querySelector(
          `div.opt[value="${lifeNumberStr}"]`
        );
        if (targetOption) {
          // Simulate the click on the option to trigger the proper custom event
          targetOption.click();
        }
      }
    }

    // Try select-input (dropdown selects)
    const numberSelectInput = document.querySelector(
      'select-input select[data-option_type*="Number"], select-input select[data-option_type*="number"], select-input select[data-option_type*="Zahl"], select-input select[data-option_type*="zahl"]'
    );
    if (numberSelectInput) {
      const targetOption = numberSelectInput.querySelector(
        `option[value="${lifeNumberStr}"]`
      );
      if (targetOption) {
        numberSelectInput.value = lifeNumberStr;
        numberSelectInput.setAttribute("selected_id", lifeNumberStr);

        // Trigger change event to update any variant selection logic
        const changeEvent = new Event("change", { bubbles: true });
        numberSelectInput.dispatchEvent(changeEvent);
      }
    }

    // Hide the cart message when calculation is complete
    const cartMessage = document.querySelector(".numerology-cart-message");
    if (cartMessage) {
      cartMessage.classList.add("hidden");
    }

    // Show cart buttons using CSS override approach
    this.addCartButtonShowStyles();

    // Scroll to result
    this.resultElement.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  resetCalculator() {
    // Reset calculate button to original state
    const calculateBtn = this.wrapper.querySelector(".calculate-btn");
    if (calculateBtn) {
      calculateBtn.classList.remove(
        "bg-white",
        "text-anna-black",
        "border",
        "border-anna-black",
        "hover:!bg-anna-black",
        "hover:text-white"
      );
      calculateBtn.classList.add(
        "bg-anna-black",
        "text-white",
        "hover:bg-anna-black"
      );
      calculateBtn.textContent =
        window.numerologyTexts?.buttons?.calculate || "Berechnen";
      calculateBtn.removeAttribute("data-state");
    }

    // Unlock dropdowns first
    this.unlockDropdowns();

    // Reset dropdowns
    this.dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector(".dropdown-trigger");
      const text = dropdown.querySelector(".dropdown-text");
      const dropdownType = dropdown.dataset.dropdown;

      trigger.dataset.value = "";

      // Reset text color to gray for placeholders
      text.classList.remove("text-anna-black");
      text.classList.add("text-anna-gray");

      // Reset text based on dropdown type - using simple placeholders
      switch (dropdownType) {
        case "day":
          text.textContent = "TT";
          break;
        case "month":
          text.textContent = "MM";
          break;
        case "year":
          text.textContent = "JJJJ";
          break;
      }
    });

    // Hide result and steps
    this.resultElement.classList.add("hidden");
    if (this.stepsElement) {
      this.stepsElement.classList.add("hidden");
    }

    // Remove CSS styles that force cart buttons to show
    this.removeCartButtonStyles();

    // Reset number selection to first option when form is cleared
    const numberOptionsInput = document.querySelector(
      'options-input select[data-option_type*="Number"], options-input select[data-option_type*="number"], options-input select[data-option_type*="Zahl"], options-input select[data-option_type*="zahl"]'
    );
    if (numberOptionsInput) {
      const optionsInputElement = numberOptionsInput.closest("options-input");
      const firstOption = optionsInputElement.querySelector(
        "div.opt:first-child"
      );

      if (firstOption && optionsInputElement) {
        // Simulate click on first option to reset properly
        firstOption.click();
      }
    }

    // Show the cart message when form is cleared
    const cartMessage = document.querySelector(".numerology-cart-message");
    if (cartMessage) {
      cartMessage.classList.remove("hidden");
    }

    // Close all dropdowns
    this.closeAllDropdowns();
  }
}

// Note: Initialization is now handled by the product-form module

export default NumerologyCalculatorInline;
